import { Button, Card, CardBody, TextField } from '@fold-and-flow/ui'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { signIn } from '#lib/auth'
import { trpc } from '#lib/trpc'

interface Props {
  token: string
}

export function AcceptInvitePage({ token }: Props) {
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    data: invite,
    isLoading,
    error,
  } = trpc.invite.validate.useQuery({ token }, { enabled: token.length > 0, retry: false })

  const accept = trpc.invite.accept.useMutation()

  async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault()
    setFormError(null)

    if (password !== confirm) {
      setFormError('Passwords do not match.')
      return
    }

    setIsSubmitting(true)
    try {
      const result = await accept.mutateAsync({ token, password })
      const signInResult = await signIn.email({ email: result.email, password })
      if (signInResult.error) {
        setFormError(signInResult.error.message ?? 'Sign-in failed. Try logging in manually.')
      } else {
        await navigate({ to: '/' })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong.'
      setFormError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  function renderContent() {
    if (!token) {
      return <InvalidLink message="No invite token found in this link." />
    }

    if (isLoading) {
      return <p className="text-center text-warm-gray-400 text-sm py-8">Validating invite link…</p>
    }

    if (error || !invite) {
      const message =
        error instanceof Error ? error.message : 'This invite link is invalid or has expired.'
      return <InvalidLink message={message} />
    }

    const activeError = formError ?? (accept.error instanceof Error ? accept.error.message : null)

    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div>
          <h2 className="font-serif text-2xl text-warm-gray-900 mb-1">Welcome, {invite.name}</h2>
          <p className="text-sm text-warm-gray-400">
            Set a password to activate your account for{' '}
            <span className="text-warm-gray-600">{invite.email}</span>
          </p>
        </div>

        {activeError ? (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
            {activeError}
          </div>
        ) : null}

        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          minLength={8}
          required
        />
        <TextField
          label="Confirm password"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          minLength={8}
          required
        />
        <Button type="submit" size="lg" loading={isSubmitting} className="mt-2">
          Create my account
        </Button>
      </form>
    )
  }

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <h1 className="font-serif text-3xl text-warm-gray-900">Fold & Flow</h1>
          <p className="text-warm-gray-400 mt-2 text-sm">Your personalized yoga practice</p>
        </div>
        <Card elevated>
          <CardBody>{renderContent()}</CardBody>
        </Card>
      </div>
    </div>
  )
}

function InvalidLink({ message }: { message: string }) {
  return (
    <div className="flex flex-col gap-4 py-2">
      <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
        {message}
      </div>
      <p className="text-sm text-warm-gray-400 text-center">
        Ask your instructor for a new invitation.
      </p>
    </div>
  )
}
